import { Building2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { getSideNavLinks } from "./nav-links";
import NavItem from "./nav-item";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "@/config/paths";
import useCurrentUser from "@/store/useCurrentUser";
import type { UserRole } from "@/types/user";
import { usePendingMilestoneCount } from "@/features/milestones/api/milestones";
import { useUnbilledCount } from "@/features/change-orders/api/change-orders-api";

function SideNav() {
  const [collapsed, setCollapsed] = useState(false);
  const user = useCurrentUser((state: any) => state.user);
  const role = user?.role as UserRole | undefined;

  const { data: milestoneData } = usePendingMilestoneCount();
  const pendingMilestoneCount = milestoneData?.data?.count;

  const { data: unbilledData } = useUnbilledCount();
  const unbilledChangeOrderCount = unbilledData?.count;

  const navLinks = getSideNavLinks(role, pendingMilestoneCount, unbilledChangeOrderCount);
  const navigate = useNavigate();

  return (
    <aside
      className={`hidden md:flex flex-col shrink-0 z-20 transition-all duration-200 border-r border-(--color-border-default) bg-(--color-bg-subtle) ${
        collapsed ? "w-20" : "w-60"
      }`}
    >
      {/* ── Logo / brand ─────────────────────────────────────────── */}
      <div className="h-14 flex items-center gap-2.5 px-3 shrink-0">
        <button
          onClick={() => navigate(paths.home.getHref())}
          className="flex items-center gap-2.5 min-w-0"
        >
          <div className="w-8 h-8 rounded-lg bg-(--color-warning-500) flex items-center justify-center shrink-0">
            <Building2 className="w-4.5 h-4.5 text-white" />
          </div>
          {!collapsed && (
            <span className="text-base font-semibold text-(--color-text-primary) whitespace-nowrap">
              ops
            </span>
          )}
        </button>
      </div>

      {/* ── Navigation items ──────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-2 space-y-0.5">
        {navLinks.map((link) => (
          <NavItem
            key={link.label}
            link={link}
            collapsed={collapsed}
            onClick={(href) => navigate(href)}
          />
        ))}
      </nav>

      {/* Collapse toggle (desktop only, not tablet) */}
      <div className="border-t border-(--color-border-default) pl-2.5 pr-6 py-2 space-y-0.5">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className={`${collapsed ? "m-auto" : ""} flex items-center justify-center w-8 h-8 rounded-md text-(--color-text-tertiary) hover:bg-(--color-bg-muted) hover:text-(--color-text-secondary) transition-colors`}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </button>
      </div>
    </aside>
  );
}

export default SideNav;
